import React from 'react';

import ExpandMoreIcon from '@mui/icons-material/ExpandMore';

import FieldHelpIcon from 'components/FieldHelpIcon';

import { OrgUnit, isIntake } from '../types';

import {
  ExpandToggle,
  NameCell,
  RowCount,
  TreeHeader,
  TreeHeaderCount,
  TreeHeaderMenuSlot,
  TreeHeaderUnit,
  UnitName,
  UnitRowShell,
} from './styles';

/** Intake stays for good: records land there whenever nothing says which unit they belong to. */
const INTAKE_INFO =
  'Records land here when nothing says which unit they belong to. Only you see them here. Move each one to the right unit.';

const PEOPLE_IN_UNIT_INFO = 'People whose home unit this is, not counting the units under it.';

export interface UnitTreeProps {
  units: OrgUnit[];
  selectedId?: string | null;
  expandedIds: Set<string>;
  onSelect?: (unitId: string) => void;
  onToggleExpand: (unitId: string) => void;
  countFor?: (unit: OrgUnit) => number;
  renderMenu?: (unit: OrgUnit) => React.ReactNode;
}

interface UnitRowProps {
  unit: OrgUnit;
  depth: number;
  selected: boolean;
  expanded: boolean;
  count?: number;
  menu?: React.ReactNode;
  onSelect?: () => void;
  onToggleExpand: () => void;
}

const UnitRow: React.FunctionComponent<UnitRowProps> = ({
  unit,
  depth,
  selected,
  expanded,
  count,
  menu,
  onSelect,
  onToggleExpand,
}) => {
  // The top unit is never foldable: collapsing it would hide the whole structure.
  const hasChildren = !!(unit.children && unit.children.length) && unit.kind !== 'root';

  const handleKeyDown = (event: React.KeyboardEvent): void => {
    if (event.target !== event.currentTarget) return;
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      onSelect?.();
    }
    if (event.key === 'ArrowRight' && hasChildren && !expanded) onToggleExpand();
    if (event.key === 'ArrowLeft' && hasChildren && expanded) onToggleExpand();
  };

  // With a menu, the name is the selecting control, so the menu button is not nested inside another button.
  const control = {
    role: onSelect ? 'button' : undefined,
    tabIndex: onSelect ? 0 : undefined,
    'aria-expanded': onSelect && hasChildren ? expanded : undefined,
    'aria-current': selected ? ('true' as const) : undefined,
    onKeyDown: onSelect ? handleKeyDown : undefined,
  };

  return (
    <UnitRowShell
      $depth={depth}
      $selected={selected}
      $selectable={!!onSelect}
      {...(menu ? {} : control)}
      aria-label={unit.name}
      data-unit-row={menu ? undefined : unit.uuid}
      data-testid={`unit-row-${unit.uuid}`}
      onClick={onSelect}
    >
      <NameCell $depth={depth}>
        <ExpandToggle
          type="button"
          $visible={hasChildren}
          $expanded={expanded}
          aria-label={expanded ? `Collapse ${unit.name}` : `Expand ${unit.name}`}
          onClick={(event) => {
            event.stopPropagation();
            onToggleExpand();
          }}
        >
          <ExpandMoreIcon fontSize="small" />
        </ExpandToggle>

        <UnitName
          $selected={selected}
          title={unit.name}
          {...(menu ? { ...control, 'aria-label': unit.name, 'data-unit-row': unit.uuid } : {})}
        >
          {unit.name}
          {isIntake(unit) ? <FieldHelpIcon name={unit.name} helpText={INTAKE_INFO} /> : null}
        </UnitName>
      </NameCell>
      {count !== undefined ? <RowCount>{isIntake(unit) ? null : count}</RowCount> : null}
      {menu}
    </UnitRowShell>
  );
};

const UnitTree: React.FunctionComponent<UnitTreeProps> = ({
  units,
  selectedId,
  expandedIds,
  onSelect,
  onToggleExpand,
  countFor,
  renderMenu,
}) => {
  const renderRows = (list: OrgUnit[], depth: number): React.ReactElement[] =>
    list.flatMap((unit) => {
      const isExpanded = expandedIds.has(unit.uuid);
      const row = (
        <UnitRow
          key={unit.uuid}
          unit={unit}
          depth={depth}
          selected={selectedId === unit.uuid}
          expanded={isExpanded}
          count={countFor ? countFor(unit) : undefined}
          menu={renderMenu ? renderMenu(unit) : undefined}
          onSelect={onSelect ? () => onSelect(unit.uuid) : undefined}
          onToggleExpand={() => onToggleExpand(unit.uuid)}
        />
      );
      return isExpanded && unit.children?.length ? [row, ...renderRows(unit.children, depth + 1)] : [row];
    });

  return (
    <>
      {countFor ? (
        <TreeHeader>
          <TreeHeaderUnit>Unit</TreeHeaderUnit>
          <TreeHeaderCount>
            People in unit
            <FieldHelpIcon name="People in unit" helpText={PEOPLE_IN_UNIT_INFO} />
          </TreeHeaderCount>
          {renderMenu ? <TreeHeaderMenuSlot /> : null}
        </TreeHeader>
      ) : null}
      <div>{renderRows(units, 0)}</div>
    </>
  );
};

export default UnitTree;
